import { LOCALES, type Locale, PRODUCTS } from '@bb/shared';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { changeLocale, ensureNamespace } from '@/lib/i18n';

export const Route = createFileRoute('/')({
  loader: () => ensureNamespace('legal'),
  component: LandingPage,
});

function LandingPage() {
  const { t, i18n } = useTranslation(['common', 'legal']);
  const products = Object.values(PRODUCTS);
  return (
    <main className="mx-auto max-w-3xl px-4 py-10 sm:py-16">
      <header className="mb-8 flex flex-wrap items-center justify-between gap-3 sm:mb-10">
        <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">{t('app.name')}</h1>
        <nav className="flex gap-2 text-sm">
          {LOCALES.map((locale) => (
            <button
              key={locale}
              type="button"
              onClick={() => void changeLocale(locale as Locale)}
              className={
                i18n.language === locale
                  ? 'flex min-h-11 items-center rounded px-2 font-medium underline sm:min-h-0 sm:py-1'
                  : 'flex min-h-11 items-center rounded px-2 text-neutral-500 hover:text-neutral-900 sm:min-h-0 sm:py-1'
              }
            >
              {t(`language.${locale}`)}
            </button>
          ))}
        </nav>
      </header>
      <ul className="grid gap-4 sm:grid-cols-2">
        {products.map((product) => (
          <li key={product.id} className="rounded-lg border border-neutral-200 bg-white p-5">
            <h2 className="text-lg font-medium">{t(`products.${product.id}.name`)}</h2>
            <p className="mt-1 text-sm text-neutral-600">{t(`products.${product.id}.tagline`)}</p>
            {product.status === 'available' ? (
              <Link
                to="/login"
                search={{ product: product.id }}
                className="mt-4 inline-flex min-h-11 items-center justify-center rounded bg-neutral-900 px-4 py-2 text-sm font-medium text-white sm:min-h-0 sm:py-1.5"
              >
                {t('actions.continue')}
              </Link>
            ) : (
              <span className="mt-4 inline-block text-xs text-neutral-400">
                {t('status.comingSoon')}
              </span>
            )}
          </li>
        ))}
      </ul>
      {/* Google's OAuth review expects the privacy policy to be reachable here. */}
      <footer className="mt-12 flex flex-wrap gap-x-4 gap-y-1 border-t border-neutral-200 pt-4 text-xs text-neutral-500">
        <Link to="/terms" className="flex min-h-11 items-center underline sm:min-h-0">
          {t('legal:terms.title')}
        </Link>
        <Link to="/privacy" className="flex min-h-11 items-center underline sm:min-h-0">
          {t('legal:privacy.title')}
        </Link>
      </footer>
    </main>
  );
}
